import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FaSearch, FaTimes, FaSpinner } from 'react-icons/fa';
import { SEARCH_QCM_LAB_API_BASE_URL, authHeaders } from '../../services/api';

const SearchBox = ({ examType, examId, userId, onItemSelect }) => {
    const { t } = useTranslation();
    const [searchText, setSearchText] = useState('');
    const [results, setResults] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showResults, setShowResults] = useState(false);
    const [error, setError] = useState(null);
    
    const searchRef = useRef(null);
    const resultsRef = useRef(null);

    // Déterminer le label selon le type d'examen
    const getSearchLabel = () => {
        if (examType === 'lab') {
            return t('search_a_lab', 'Search a Lab');
        }
        return t('search_a_qcm', 'Search a QCM');
    };

    // Fonction de recherche
    const performSearch = async (text) => {
        if (!text.trim() || text.length < 3) {
            setResults([]);
            setShowResults(false);
            return;
        }

        setIsLoading(true);
        setError(null);

        // Debug logs
        console.log('🔍 SearchBox performSearch:', {
            text,
            examId,
            examType,
            userId,
            searchUrl: `${SEARCH_QCM_LAB_API_BASE_URL}/api/search/qcm_or_instruction`
        });

        try {
            const payload = {
                userid: userId || 'anonymous_user',
                exam_id: examId || 'AWS_Certified_Machine_Learning',
                text: text,
                questionType: examType || 'qcm'
            };
            
            console.log('🔍 SearchBox payload:', payload);

            const response = await fetch(`${SEARCH_QCM_LAB_API_BASE_URL}/api/search/qcm_or_instruction`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify(payload)
            });

            const data = await response.json();
            console.log('🔍 SearchBox response:', data);

            if (data.success) {
                setResults(data.results);
                setShowResults(data.results.length > 0);
                console.log('🔍 SearchBox results set:', data.results.length, 'items, showResults:', data.results.length > 0);
            } else {
                setError(data.error || 'Erreur de recherche');
                setResults([]);
                setShowResults(false);
                console.log('🔍 SearchBox error:', data.error);
            }
        } catch (err) {
            console.error('Erreur de recherche:', err);
            setError('Erreur de connexion');
            setResults([]);
            setShowResults(false);
        } finally {
            setIsLoading(false);
        }
    };

    // Gestion de la saisie (sans recherche automatique)
    const handleInputChange = (e) => {
        const value = e.target.value;
        setSearchText(value);
        
        // Ne plus faire de recherche automatique - attendre Entrée
        if (value.length < 3) {
            setResults([]);
            setShowResults(false);
        }
    };

    // Gestion de la touche Entrée
    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            performSearch(searchText);
        } else if (e.key === 'Escape') {
            setShowResults(false);
            setSearchText('');
            searchRef.current?.blur();
        }
    };

    // Sélection d'un résultat
    const handleResultClick = async (result) => {
        setShowResults(false);
        setIsLoading(true);

        try {
            // Déléguer au service qui gère load_item + fallback Node API
            const { fetchQCMContent } = require('../../services/api');
            const item = await fetchQCMContent(result.id);
            if (item) {
                onItemSelect(item, 'qcm');
                setSearchText('');
            } else {
                setError('Item not found');
            }
        } catch (err) {
            console.error('Erreur de chargement:', err);
            setError(err?.message || 'Erreur de connexion');
        } finally {
            setIsLoading(false);
        }
    };

    // Fermer les résultats en cliquant ailleurs
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (resultsRef.current && !resultsRef.current.contains(event.target) &&
                searchRef.current && !searchRef.current.contains(event.target)) {
                setShowResults(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Effacer la recherche
    const clearSearch = () => {
        setSearchText('');
        setResults([]);
        setShowResults(false);
        setError(null);
        searchRef.current?.focus();
    };

    return (
        <div className="relative">
            {/* Boîte de recherche */}
            <div className="relative">
                <div className="flex items-center bg-gray-700 rounded-lg border border-gray-600 focus-within:border-blue-500 transition-colors">
                    <FaSearch className="ml-3 text-gray-400" />
                    <input
                        ref={searchRef}
                        type="text"
                        value={searchText}
                        onChange={handleInputChange}
                        onKeyDown={handleKeyPress}
                        placeholder={getSearchLabel()}
                        className="flex-1 px-3 py-2 bg-transparent text-white placeholder-gray-400 focus:outline-none"
                        disabled={isLoading}
                    />
                    {isLoading && (
                        <FaSpinner className="mr-3 text-blue-400 animate-spin" />
                    )}
                    {searchText && !isLoading && (
                        <button
                            onClick={clearSearch}
                            className="mr-3 text-gray-400 hover:text-white transition-colors"
                        >
                            <FaTimes />
                        </button>
                    )}
                </div>
            </div>

            {/* Résultats de recherche */}
            {showResults && (
                <div
                    ref={resultsRef}
                    className="absolute top-full left-0 right-0 mt-1 bg-gray-800 rounded-lg shadow-lg border border-gray-600 max-h-96 overflow-y-auto z-50"
                >
                    {results.map((result, index) => (
                        <div
                            key={result.id}
                            onClick={() => handleResultClick(result)}
                            className="p-3 hover:bg-gray-700 cursor-pointer border-b border-gray-700 last:border-b-0 transition-colors"
                        >
                            <div className="flex justify-between items-start mb-2">
                                <div className="flex-1">
                                    {/* Question tronquée */}
                                    <p className="text-white text-sm font-medium mb-1 line-clamp-2">
                                        {result.question || 'Question non disponible'}
                                    </p>
                                    
                                    {/* Métadonnées */}
                                    {(result.domain || result.task) && (
                                        <p className="text-gray-400 text-xs mb-1">
                                            {[result.domain, result.task].filter(Boolean).join(' | ')}
                                        </p>
                                    )}
                                </div>
                                
                                {/* Score */}
                                <span className="text-xs bg-blue-600 text-white px-2 py-1 rounded ml-2 flex-shrink-0">
                                    {result.score}%
                                </span>
                            </div>
                            
                            {/* Keywords */}
                            {result.keywords && result.keywords.length > 0 && (
                                <div className="flex flex-wrap gap-1">
                                    {result.keywords.slice(0, 5).map((keyword, idx) => (
                                        <span
                                            key={idx}
                                            className="text-xs bg-gray-600 text-gray-300 px-2 py-1 rounded"
                                        >
                                            {keyword}
                                        </span>
                                    ))}
                                    {result.keywords.length > 5 && (
                                        <span className="text-xs text-gray-500">
                                            +{result.keywords.length - 5} more
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                    
                    {/* Message si pas de résultats */}
                    {results.length === 0 && !isLoading && (
                        <div className="p-4 text-center text-gray-400">
                            {t('no_results_found', 'Aucun résultat trouvé')}
                        </div>
                    )}
                </div>
            )}

            {/* Message d'erreur */}
            {error && (
                <div className="absolute top-full left-0 right-0 mt-1 p-3 bg-red-900 text-red-200 rounded-lg border border-red-700 z-50">
                    {error}
                </div>
            )}
        </div>
    );
};

export default SearchBox;
